import moment from 'moment';

/* eslint @typescript-eslint/camelcase: 0 */
import {
  PatientCreatedApi,
  PatientCreatedResponse,
  EligibilityCheckResponse,
  EligibilityCheckResponseApi,
  Profile,
} from '../../interfaces/register';
import { V2PatientProfile } from '../../interfaces/patient';
import { formatDate } from '../../utils/shared/normalize';

export const mapEligibilityCheck = ({
  disable_ineligible_member_registration,
  affiliation_description,
  affiliation_type,
  affiliation_id,
  first_name,
  last_name,
  birthdate,
  member_id,
  business_unit_id,
  insurance_payer_id,
  dependent,
  primary_first_name,
  primary_last_name,
  primary_gender,
  primary_member_id,
  primary_birthdate,
  group_list,
  existing_account,
  ...rest
}: EligibilityCheckResponseApi): EligibilityCheckResponse => {
  const isDependent: string = dependent ? 'true' : 'false';

  return {
    disableIneligibleRegistration: disable_ineligible_member_registration,
    affiliationName: affiliation_description,
    affiliationType: affiliation_type,
    affiliationId: affiliation_id,
    firstName: first_name,
    lastName: last_name,
    dobDay: moment(birthdate).format('DD'),
    dobMonth: moment(birthdate).format('MM'),
    dobYear: moment(birthdate).format('YYYY'),
    memberId: member_id,
    businessUnitId: business_unit_id,
    insurancePayerId: insurance_payer_id,
    isDependent,
    primaryFirstName: primary_first_name,
    primaryLastName: primary_last_name,
    primaryGender: primary_gender,
    primaryMemberId: primary_member_id,
    primaryDobDay: primary_birthdate && moment(primary_birthdate).format('DD'),
    primaryDobMonth:
      primary_birthdate && moment(primary_birthdate).format('MM'),
    primaryDobYear:
      primary_birthdate && moment(primary_birthdate).format('YYYY'),
    groupList: group_list,
    existingAccount: existing_account ?? false,
    ...rest,
  };
};

export const mapPatient = (
  patient: PatientCreatedApi
): PatientCreatedResponse => {
  return {
    id: patient.id,
    firstName: patient.first_name,
    lastName: patient.last_name,
    gender: patient.gender,
    affiliationName: patient.affiliation_description,
    zip: patient.zip,
    dobDay: moment(patient.birthdate).format('DD'),
    dobMonth: moment(patient.birthdate).format('MM'),
    dobYear: moment(patient.birthdate).format('YYYY'),
    eligible: patient.eligible,
    affiliationId: patient.affiliation_id,
    lastEligibilityCheckId: patient.last_eligibility_check_id,
    eligibilityChecks: patient.eligibility_checks.map((check) =>
      mapEligibilityCheck(check)
    ),
  };
};

export interface PatientBody {
  first_name: string;
  last_name: string;
  gender: string;
  zip: string;
  affiliation_id?: string;
  phone: string;
}

export const mapPatientApi = (values: Profile): PatientBody => {
  return {
    first_name: values.firstName,
    last_name: values.lastName,
    gender: values.gender,
    zip: values.zip,
    affiliation_id: values.affiliationId,
    phone: values.phoneNumber,
  };
};

export interface V2PatientBody {
  first_name: string;
  last_name: string;
  gender: string;
  email: string;
  password: string;
  birthdate: string;
  zip: string;
  phone?: string;
  terms_of_service: boolean;
  affiliation_id?: number;
  display_ineligible_error?: boolean;
  eligibility_check_token?: string;
}

export const mapV2PatientApi = (values: V2PatientProfile): V2PatientBody => {
  return {
    first_name: values.firstName,
    last_name: values.lastName,
    gender: values.gender,
    email: values.email,
    password: values.password,
    birthdate: formatDate(
      `${values.dobMonth}/${values.dobDay}/${values.dobYear}`,
      'iso'
    ),
    zip: values.zip,
    phone: values.phone || '',
    terms_of_service: values.acceptTerms,
    affiliation_id: values.affiliationId,
    display_ineligible_error: values.displayIneligibleError,
    // Set only on the 270 path; undefined elsewhere leaves the body unchanged.
    eligibility_check_token: values.eligibilityCheckToken,
  };
};
